<template>
  <v-sheet color="snow" rounded="xl" class="pa-4">
    <v-container :fluid="true">
      <v-container class="pa-0">
        <v-row>
          <v-col>
            <div v-if="props.title" class="text-h4 text-primary">{{ props.title }}</div>
            <div v-if="errorMsg" class="text-error">{{ errorMsg }}</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="toFaxNumber"
              label="To Fax Number"
              variant="outlined"
              bg-color="white"
              :rules="[requiredRule, phoneNumberRule]" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="templateItems.length > 0">
            <v-combobox
              variant="outlined"
              bg-color="white"
              :items="templateItems"
              label="Select a fax template"
              :model-value="selectedTemplate"
              @update:model-value="onTemplateSelected" />
          </v-col>
        </v-row>
        <v-row v-if="props.attachmentOptions.length > 0">
          <v-col>
            <v-combobox
              variant="outlined"
              bg-color="white"
              :items="props.attachmentOptions"
              label="Select an attachment"
              :model-value="selectedAttachment"
              clearable
              @update:model-value="onAttachmentSelected" />
            <v-checkbox
              v-if="supportingDocumentsUrls.length > 0"
              v-model="includeSupportingDocuments"
              label="Include supporting documents"
              hide-details
              class="mt-2" />
            <div
              v-if="includeSupportingDocuments && supportingDocumentsUrls.length > 0 && selectedAttachment"
              class="text-caption text-medium-emphasis mt-1">
              {{ getAttachmentSummary() }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea v-model="content" label="Content" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="props.cancelButton" cols="4">
            <v-btn-lh :loading="loading" @click="onCancel">Cancel</v-btn-lh>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto" align="right">
            <v-btn-lh v-if="downloadLinks.length > 0" :disabled="true" :loading="loading">Attached</v-btn-lh>
            <template v-else>
              <v-btn-lh v-if="!showFileUpload" :loading="loading" @click="onAttachFile">Attach a File</v-btn-lh>
              <UploadFileComponent v-if="showFileUpload" @download-link="onDownloadLink" />
            </template>
          </v-col>
          <v-col cols="auto" align="right">
            <v-btn-lh
              :disabled="(!content && !selectedAttachment) || !toFaxNumber"
              :loading="loading"
              @click="onPreview">
              Preview
            </v-btn-lh>
          </v-col>
          <v-col cols="auto" align="right">
            <v-btn-lh :disabled="(!content && !selectedAttachment) || !toFaxNumber" :loading="loading" @click="onSend">
              {{ props.sendButtonText }}
            </v-btn-lh>
          </v-col>
        </v-row>
        <v-dialog v-model="showPreview" width="90%" height="90%">
          <v-card>
            <v-card-title>Preview</v-card-title>
            <v-card-subtitle> Send to: {{ toFaxNumber }} </v-card-subtitle>
            <v-card-text>
              <v-alert
                v-if="selectedAttachment?.value?.type === 'flattened_form'"
                type="info"
                variant="tonal"
                prominent
                class="mb-4">
                <template #prepend>
                  <v-icon size="large">mdi-file-document-outline</v-icon>
                </template>
                <div class="text-h6 font-weight-bold mb-1">Form Attachment</div>
                <div class="text-body-1">Filled prior authorization form will be attached to the fax.</div>
              </v-alert>
              <iframe :srcdoc="previewContent" width="100%" height="100%" frameborder="0"></iframe>
            </v-card-text>
            <v-card-actions>
              <v-btn-lh @click="showPreview = false">Close</v-btn-lh>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </v-container>
  </v-sheet>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { templateService } from "@/services/templateService";
import { eFaxService } from "@/services/eFaxService";
import { IntakeOrder, IntakeDocument } from "@/models/intake";
import { ClientTemplate } from "@/models/templates";
import { requiredRule } from "@/utils/form";
import { isPhoneNumberValid } from "@/utils/validators";
import UploadFileComponent from "@/components/UploadFileComponent.vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  types: {
    type: Array,
    required: false,
    default: () => [],
  },
  toFaxNumber: {
    type: String,
    required: false,
    default: null,
  },
  intakeOrder: {
    type: IntakeOrder,
    required: false,
    default: null,
  },
  intakeDocument: {
    type: IntakeDocument,
    required: false,
    default: null,
  },
  orderInfo: {
    type: Object,
    required: false,
    default: null,
  },
  cancelButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  sendButtonText: {
    type: String,
    required: false,
    default: "Send Fax",
  },
  deferSend: {
    type: Boolean,
    required: false,
    default: false,
  },
  attachmentOptions: {
    type: Array,
    required: false,
    default: () => [],
  },
  preselectedAttachment: {
    type: Object,
    required: false,
    default: null,
  },
  prefilledContent: {
    type: String,
    required: false,
    default: null,
  },
  frontendTemplateName: {
    type: String,
    required: false,
    default: null,
  },
  frontendTemplate: {
    type: Object,
    required: false,
    default: null,
  },
  supportingDocuments: {
    type: Array,
    required: false,
    default: () => [],
  },
});
const emit = defineEmits(["onComplete", "onPreSend", "onFrontendTemplateSelected"]);

const templates = ref([]);
const templateItems = ref([]);
const showFileUpload = ref(false);
const selectedTemplate = ref(null);
const content = ref("");
const errorMsg = ref("");
const loading = ref(false);
const toFaxNumber = ref(props.toFaxNumber ? props.toFaxNumber : "");
const downloadLinks = ref([]);

const phoneNumberRule = (v) => {
  if (!v) return true; // requiredRule handles empty values
  return isPhoneNumberValid(v) ? true : "Please enter a valid fax number (10 digits)";
};
const showPreview = ref(false);
const previewContent = ref("");
const selectedAttachment = ref(props.preselectedAttachment || null);
const includeSupportingDocuments = ref(true);

const supportingDocumentsUrls = computed(() => {
  return props.supportingDocuments || [];
});

onMounted(() => {
  // Fetch backend templates if types are provided
  // Note: Frontend template is handled by the watcher (which runs immediately)
  if (props.types && props.types.length > 0) {
    fetchTemplates();
  }
});

watch(
  () => props.types,
  () => {
    if (props.types && props.types.length > 0 && !props.prefilledContent) {
      fetchTemplates();
    }
  },
  { immediate: false }
);

const onTemplateSelected = (selected) => {
  selectedTemplate.value = selected;
  // For frontend templates, set content and emit event
  if (selected.value === "frontend-template" && props.frontendTemplate) {
    content.value = props.frontendTemplate.content || "";
    emit("onFrontendTemplateSelected", props.frontendTemplate);
    return;
  }
  const template = templates.value.find((t) => t.id === selected.value);
  content.value = template?.editableContent ?? "";
};

watch(
  () => props.frontendTemplate,
  (newTemplate) => {
    if (newTemplate) {
      // Add or update frontend template in the list
      const existingIndex = templateItems.value.findIndex((item) => item.value === "frontend-template");
      const frontendTemplateItem = {
        title: newTemplate.name,
        value: "frontend-template",
      };

      if (existingIndex >= 0) {
        templateItems.value[existingIndex] = frontendTemplateItem;
      } else {
        templateItems.value.unshift(frontendTemplateItem);
      }

      // Auto-select frontend template when it's provided
      selectedTemplate.value = frontendTemplateItem;
      onTemplateSelected(frontendTemplateItem);
    } else {
      // Remove frontend template from list if it's no longer provided
      const existingIndex = templateItems.value.findIndex((item) => item.value === "frontend-template");
      if (existingIndex >= 0) {
        templateItems.value.splice(existingIndex, 1);
        // If it was selected, clear selection
        if (selectedTemplate.value?.value === "frontend-template") {
          selectedTemplate.value = templateItems.value.length > 0 ? templateItems.value[0] : null;
          content.value = "";
        }
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.toFaxNumber,
  (newValue) => {
    if (newValue !== undefined && newValue !== null) {
      toFaxNumber.value = newValue;
    }
  },
  { immediate: true }
);

watch(
  () => props.preselectedAttachment,
  (newValue) => {
    if (newValue) {
      selectedAttachment.value = newValue;
    }
  },
  { immediate: true }
);

const fetchTemplates = async () => {
  try {
    loading.value = true;
    errorMsg.value = "";
    const response = await templateService.getTemplates({
      templateTypes: props.types,
      intakeOrderId: props.intakeOrder?.id,
      orderInfoKey: props.orderInfo?.orderKey,
      intakeDocumentKey: props.intakeDocument?.documentKey,
    });
    templates.value = response.results.map((t) => new ClientTemplate(t));
    // Preserve frontend template if it exists
    const frontendTemplateItem = templateItems.value.find((item) => item.value === "frontend-template");

    // Add backend templates
    const backendTemplateItems = templates.value.map((t) => ({
      title: t.name ?? t.templateType,
      value: t.id,
    }));

    // Combine: frontend template first (if exists), then backend templates
    templateItems.value = frontendTemplateItem ? [frontendTemplateItem, ...backendTemplateItems] : backendTemplateItems;

    // Select frontend template if it exists, otherwise select first backend template
    if (frontendTemplateItem) {
      selectedTemplate.value = frontendTemplateItem;
      onTemplateSelected(frontendTemplateItem);
    } else if (templateItems.value.length > 0) {
      selectedTemplate.value = templateItems.value[0];
      onTemplateSelected(selectedTemplate.value);
    }
  } catch (error) {
    errorMsg.value = `Error fetching templates: ${error.message}`;
  } finally {
    loading.value = false;
  }
};

const onAttachFile = () => {
  showFileUpload.value = true;
};

const onDownloadLink = (download) => {
  downloadLinks.value.push(download.link);
};

const onAttachmentSelected = (selected) => {
  // Allow clearing selection (null or undefined)
  selectedAttachment.value = selected || null;
};

const getAttachmentSummary = () => {
  if (!selectedAttachment.value || supportingDocumentsUrls.value.length === 0) {
    return "";
  }
  const attachmentName = selectedAttachment.value.title || "Filled Form";
  const docCount = supportingDocumentsUrls.value.length;
  return `${attachmentName} + ${docCount} supporting document${docCount !== 1 ? "s" : ""}`;
};

const resetForm = () => {
  content.value = "";
  selectedAttachment.value = null;
  downloadLinks.value = [];
  showPreview.value = false;
  previewContent.value = "";
  errorMsg.value = "";
};

const onSend = async () => {
  try {
    errorMsg.value = "";
    loading.value = true;

    // Build attachment data if selected
    const attachmentData = selectedAttachment.value?.value
      ? {
          type: selectedAttachment.value.value.type,
          value: selectedAttachment.value.value.type === "response_fax" ? selectedAttachment.value.value.s3_url : null,
        }
      : null;
    const isFlattenedFormAttachment = attachmentData?.type === "flattened_form";

    if (isFlattenedFormAttachment) {
      try {
        await new Promise((resolve, reject) => {
          emit("onPreSend", resolve, reject);
        });
      } catch (saveError) {
        errorMsg.value = "Failed to save prior authorization form before faxing. Please try again.";
        loading.value = false;
        return;
      }
    }

    const fileLinks = [...downloadLinks.value];
    if (includeSupportingDocuments.value && supportingDocumentsUrls.value.length > 0) {
      fileLinks.push(...supportingDocumentsUrls.value);
    }

    const requestData = {
      text: content.value,
      toFaxNumber: toFaxNumber.value,
      fileLinks: fileLinks,
    };
    if (selectedTemplate.value?.value && selectedTemplate.value.value !== "frontend-template") {
      requestData.templateId = selectedTemplate.value.value;
    }
    if (attachmentData) {
      requestData.attachment = attachmentData;
    }
    if (props.intakeOrder) {
      requestData.intakeOrderId = props.intakeOrder.id;
    }
    if (props.orderInfo) {
      requestData.orderInfoKey = props.orderInfo.orderKey;
    }
    if (props.intakeDocument) {
      requestData.intakeDocumentKey = props.intakeDocument.documentKey;
    }
    if (props.deferSend) {
      const isFrontendTemplate = selectedTemplate.value?.value === "frontend-template";
      const frontendTemplateCategory =
        isFrontendTemplate && props.frontendTemplate ? props.frontendTemplate.category : null;
      emit("onComplete", { sent: true, queuePayload: requestData, isFrontendTemplate, frontendTemplateCategory });
      resetForm();
      return;
    }
    const response = await eFaxService.postSendOutgoingFax(requestData);
    const isFrontendTemplate = selectedTemplate.value?.value === "frontend-template";
    const frontendTemplateCategory =
      isFrontendTemplate && props.frontendTemplate ? props.frontendTemplate.category : null;
    emit("onComplete", { sent: response, isFrontendTemplate, frontendTemplateCategory });
    // Clear form after successful send
    resetForm();
  } catch (error) {
    if (error instanceof Error && error.format) {
      errorMsg.value = error.format();
    } else {
      errorMsg.value = `Error sending fax: ${error.message || "Unknown error"}`;
    }
  } finally {
    loading.value = false;
  }
};

defineExpose({});

const onCancel = () => {
  emit("onComplete", { sent: null });
};

const onPreview = async () => {
  showPreview.value = true;
  loading.value = true;
  try {
    const attachmentData = selectedAttachment.value?.value
      ? {
          type: selectedAttachment.value.value.type,
          value: selectedAttachment.value.value.type === "response_fax" ? selectedAttachment.value.value.s3_url : null,
        }
      : null;
    const isFlattenedFormAttachment = attachmentData?.type === "flattened_form";
    const shouldPreviewAttachment = attachmentData && !isFlattenedFormAttachment;

    const baseRequestData = {
      content: content.value || "",
    };
    if (props.orderInfo?.orderKey) {
      baseRequestData.orderInfoKey = props.orderInfo.orderKey;
    }
    if (props.intakeOrder?.id) {
      baseRequestData.intakeOrderId = props.intakeOrder.id;
    }
    if (props.intakeDocument?.documentKey) {
      baseRequestData.intakeDocumentKey = props.intakeDocument.documentKey;
    }

    let response = "";

    // Handle frontend templates (no backend template)
    if (selectedTemplate.value?.value === "frontend-template") {
      if (shouldPreviewAttachment) {
        const requestPayload = { ...baseRequestData, attachment: attachmentData };
        response = await templateService.getFaxContent(requestPayload);
      } else if (baseRequestData.content) {
        response = await templateService.getFaxContent(baseRequestData);
      }
    } else if (selectedTemplate.value) {
      if (shouldPreviewAttachment) {
        const requestPayload = {
          ...baseRequestData,
          attachment: attachmentData,
          template_id: selectedTemplate.value.value,
        };
        response = await templateService.getFaxContent(requestPayload);
      } else {
        response = await templateService.getTemplatePreview(selectedTemplate.value.value, content.value || "");
      }
    } else if (shouldPreviewAttachment) {
      const requestPayload = { ...baseRequestData, attachment: attachmentData };
      response = await templateService.getFaxContent(requestPayload);
    } else if (baseRequestData.content) {
      response = await templateService.getFaxContent(baseRequestData);
    }

    if (!response) {
      response = "<p style='font-family: DM Sans; color: #5E7898;'>No additional content selected for preview.</p>";
    }

    previewContent.value = response;
  } catch (error) {
    errorMsg.value = `Error fetching preview: ${error.message}`;
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped></style>
